// Shared with mju-ai-workbench/shared/schemas/resourceChanges.ts — keep the two identical. AIOE owns it.
/**
 * Change hints on the workbench's stream (25 Oct 2026), so a workbench asks for things when they change rather than
 * on a timer. AIOE sends `event: changed` on `GET /relay/v1/workbench/stream` naming what changed, ids only and never
 * content; the workbench fetches only what is named, with If-None-Match, and while its stream is up keeps its timers
 * only as a 15-minute safety net. When the stream drops it returns to its usual intervals.
 *
 * `seq` counts the hints sent to this device: a gap means hints were missed, and the workbench refetches once
 * everything it would act on. After AIOE restarts it sends `resources: ["*"]` once, which means the same.
 */
import { z } from "zod";
/** The keys a workbench acts on. `<id>` is a project id. */
export const RESOURCE_CHANGE_KEYS = [
    "project:<id>",
    "board:<id>",
    "notifications",
    "memory:<id>",
    "embeddings:<id>",
    "source:<id>",
    "apps:<id>",
    "routines:<id>",
    /** A Team Work message was posted in the project (people in Team Work, 0.89.20): pull `…/messages?after=`. */
    "teamwork:<id>",
    "mesh-map",
    "policy",
    "catalogue",
    "settings-layer",
    "saved-teams",
    "fleet",
    "releases",
    "licence",
    /** The organisation's roles or role assignments changed (docs/rbac.md): read `GET /me/v1/permissions` again. */
    "roles",
    /** A held action this machine asked for was decided or lapsed (docs/rbac.md, step 6): read `GET /me/v1/held-actions/:id` now. */
    "held-actions",
    /** The person's own apps changed (D426, `GET /me/v1/apps?since=`); sent only to that person's machines. */
    "me:apps",
    /** The person's Home preferences changed (D426, `GET /me/v1/preferences/home`); sent only to that person's machines. */
    "me:home",
    /** The organisation's Foundry connections or agent catalogue changed (D441): read /foundry/v1/connections and /foundry/v1/agents again. */
    "foundry",
    /** The organisation's approved Kubernetes clusters changed (D447): read /compute/v1/clusters again. */
    "compute",
];
const resourceKey = z.string().regex(/^(\*|notifications|mesh-map|policy|catalogue|settings-layer|saved-teams|fleet|releases|licence|roles|held-actions|me:apps|me:home|foundry|compute|(project|board|memory|embeddings|source|apps|routines|teamwork):[A-Za-z0-9_-]{1,100})$/);
export const resourceChangedEventSchema = z.object({
    kind: z.literal("changed"),
    resources: z.array(resourceKey).min(1).max(100),
    /** Counts hints to this device from 1; a gap (or "*") means refetch everything once. */
    seq: z.number().int().positive(),
});
//# sourceMappingURL=resourceChanges.js.map